import { Undefinable } from "@types";

/**
 * 전화번호에서 숫자만 추출
 *
 * @param phoneNumber - 전화번호 문자열
 * @returns 숫자만 포함된 문자열
 */
export const extractDigitsOnly = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, "");
};

/**
 * 비컨 형식으로 포맷팅된 국가번호 문자열에서 순수 국가번호만 추출
 *
 * 다양한 포맷 지원:
 * - "🇰🇷 South Korea [+82]" → "+82"
 * - "+82" → "+82"
 * - "[+1]" → "+1"
 *
 * @param beconCountryDialCode 비컨 형식으로 포맷팅된 국가번호 문자열
 * @returns 국가번호
 */
export const extractBeconCountryDialCode = (beconCountryDialCode: string): Undefinable<string> => {
  // [+XX] 패턴 매칭 (예: "[+82]")
  const bracketMatch = beconCountryDialCode.match(/\[(\+\d+)\]/);
  if (bracketMatch?.[1]) {
    return bracketMatch[1];
  }

  // +XX 패턴 매칭 (예: "+82")
  const dialMatch = beconCountryDialCode.match(/(\+\d+)/);
  if (dialMatch?.[1]) {
    return dialMatch[1];
  }

  return undefined;
};

/**
 * 비컨 형식의 전화번호를 삽입할 때 국가 코드와 함께 올바르게 포맷하는 함수
 *
 * 비컨 정책에 따른 처리:
 * - 한국(+82): 선행 0 추가/유지 (1012345678 → +82 01012345678)
 * - +1 국가: 선행 1 제거 (1234567890 → +1 234567890)
 * - 기타: 선행 0 제거 (0234567890 → +XX 234567890)
 *
 * @param countryDialCode 국가 코드 (예: "+82", "🇰🇷 South Korea [+82]")
 * @param phoneNumber 전화번호 (예: "01012345678")
 * @returns 국가 코드와 함께 올바르게 포맷된 전화번호 문자열
 */
export const formatBeconPhoneNumberInsert = ({
  countryDialCode,
  phoneNumber,
}: {
  countryDialCode?: string;
  phoneNumber: string;
}): string => {
  const dialCode = countryDialCode ? extractBeconCountryDialCode(countryDialCode) : null;
  const digits = extractDigitsOnly(phoneNumber);

  // 빈 값 처리
  if (!dialCode || !digits) {
    return dialCode ? dialCode : "";
  }

  // 한국(+82): 선행 0 추가/유지
  if (dialCode === "+82") {
    const normalizedDigits = digits.startsWith("0") ? digits : `0${digits}`;
    return `${dialCode} ${normalizedDigits}`;
  }

  // +1 국가: 선행 1 제거
  if (dialCode === "+1") {
    const normalizedDigits = digits.replace(/^1+/, "");
    return `${dialCode} ${normalizedDigits}`;
  }

  // 기타: 선행 0 제거
  const normalizedDigits = digits.replace(/^0+/, "");
  return `${dialCode} ${normalizedDigits}`;
};
